/* eslint-disable */
import React, { useState, useEffect } from "react";
import {
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  Box,
} from "@mui/material";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { formatTime } from "../common/util/formatter";
import ReportFilter from "./components/ReportFilter";
import { useTranslation } from "../common/components/LocalizationProvider";
import PageLayout from "../common/components/PageLayout";
import ReportsMenu from "./components/ReportsMenu";
import usePersistedState from "../common/util/usePersistedState";
import ColumnSelect from "./components/ColumnSelect";
import { useCatch } from "../reactHelper";
import useReportStyles from "./common/useReportStyles";
import TableShimmer from "../common/components/TableShimmer";
import { nativePostMessage } from "../common/components/NativeInterface";
import { sessionActions } from "../store";
import PrintHeader from "./common/PrintHeader";
import { utils, writeFileXLSX } from "xlsx";
import fetchOrThrow from "../common/util/fetchOrThrow";
import "./common/printStyles.css";

// Colunas disponíveis com suas traduções
const columnsArray = [
  ["eventTime", "costDate"],
  ["price", "sharedPrice"],
  ["type", "costCategory"],
];

// Cria o Map
const columnsMap = new Map(columnsArray);

// A stable empty object used as a fallback
const EMPTY_MAP = {}

const CostsResumeReportPage = () => {
  const classes = useReportStyles();
  const t = useTranslation();
  const dispatch = useDispatch();

  // Use stable empty object fallback and shallow equal to prevent rerenders
  const devices = useSelector((state) => state.devices.items ?? EMPTY_MAP, shallowEqual);
  const printing = useSelector((state) => state.session.printing ?? false);

  const [columns, setColumns] = usePersistedState("costsResumeColumns", [
    "eventTime",
    "type",
    "price",
  ]);
  const [items, setItems] = useState([]);
  const [hasResults, setHasResults] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedDeviceId, setSelectedDeviceId] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const selectedDeviceName = selectedDeviceId ? devices[selectedDeviceId]?.name || selectedDeviceId : undefined;

  useEffect(() => {
    if (printing) {
      requestAnimationFrame(() =>
        requestAnimationFrame(() => nativePostMessage(`print|${t('reportCostsResume')}.pdf`)),
      );
    }
  }, [printing, t]);

  // Adicionar evento de afterprint para limpar o estado de impressão
  useEffect(() => {
    const handleAfterPrint = () => {
      dispatch(sessionActions.setPrinting(false));
    };
    window.addEventListener('afterprint', handleAfterPrint);
    return () => {
      window.removeEventListener('afterprint', handleAfterPrint);
    };
  }, [dispatch]);

  const fetchData = async ({ deviceId, deviceIds = [], from, to, mail, headers } = {}) => {
    const resolvedDeviceId = deviceId ?? deviceIds[0];
    setSelectedDeviceId(resolvedDeviceId || '');
    setFromDate(from || '');
    setToDate(to || '');
    setItems([]);
    setHasResults(false);
    if (!resolvedDeviceId) {
      return [];
    }
    setLoading(true);
    try {
      const query = new URLSearchParams();
      if (from) query.append("from", from);
      if (to) query.append("to", to);
      if (mail) query.append("mail", mail);
      query.append("deviceId", resolvedDeviceId);
      const response = await fetchOrThrow(
        `/api/reports/costs_resume?${query.toString()}`,
        {
          headers,
        }
      );

      const contentType = response.headers.get("content-type") || "";
      if (contentType.includes("application/json")) {
        const data = await response.json();
        setItems(data);
        setHasResults(data.length > 0);
        return data;
      }

      window.location.assign(
        window.URL.createObjectURL(await response.blob())
      );
      setHasResults(true);
      return [];
    } catch (error) {
      console.error("Erro ao buscar dados:", error);
      setItems([]);
      setHasResults(false);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleShow = useCatch(
    async ({ deviceIds = [], from, to }) => {
      if (!deviceIds.length) {
        setItems([]);
        setHasResults(false);
        return;
      }
      await fetchData({ deviceIds, from, to });
    }
  );

  const handleExportReport = useCatch(
    async ({ deviceIds = [], from, to } = {}) => {
      const resolvedDeviceIds = deviceIds.length ? deviceIds : (selectedDeviceId ? [selectedDeviceId] : []);
      const effectiveFrom = from ?? fromDate;
      const effectiveTo = to ?? toDate;
      if (!resolvedDeviceIds.length) {
        return;
      }
      if (!items.length) {
        await fetchData({ deviceIds: resolvedDeviceIds, from: effectiveFrom, to: effectiveTo });
      }
      const table = document.querySelector("table");
      if (!table) {
        return;
      }
      requestAnimationFrame(() =>
        writeFileXLSX(utils.table_to_book(table), `${t('reportCostsResume')}.xlsx`)
      );
    }
  );

  const handlePrintReport = useCatch(
    async ({ deviceIds = [], from, to } = {}) => {
      const resolvedDeviceIds = deviceIds.length ? deviceIds : (selectedDeviceId ? [selectedDeviceId] : []);
      const effectiveFrom = from ?? fromDate;
      const effectiveTo = to ?? toDate;
      if (!resolvedDeviceIds.length) {
        return;
      }
      if (!items.length) {
        await fetchData({ deviceIds: resolvedDeviceIds, from: effectiveFrom, to: effectiveTo });
      } else {
        setSelectedDeviceId(resolvedDeviceIds[0]);
        setFromDate(effectiveFrom || '');
        setToDate(effectiveTo || '');
      }
      dispatch(sessionActions.setPrinting(true));
    }
  );

  const formatValue = (item, key) => {
    switch (key) {
      case "deviceId":
        return devices[item[key]]?.name || item[key];
      case "eventTime":
        return item[key] ? formatTime(item[key], "DD/MM/YYYY") : null;
      default:
        return item[key];
    }
  };

  // Layout para modo de impressão
  if (printing) {
    return (
      <div>
        <PrintHeader
          title={t('reportCostsResume')}
          device={selectedDeviceName}
          from={fromDate}
          to={toDate}
        />
        <div>
          <Table>
            <TableHead>
              <TableRow>
                {columns.map((key) => (
                  <TableCell key={key}>
                    {t(columnsMap.get(key) || key)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item, index) => (
                <TableRow key={`item_${index}`}>
                  {columns.map((key) => (
                    <TableCell key={key}>{formatValue(item, key)}</TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    );
  }

  // O conteúdo que será passado como children para o PageLayout
  const pageContent = (
    <div className={classes.container}>
      <div className={classes.containerMain}>
        <div className={classes.header}>
          {/* standardized export/print/agenda menu */}
          <ReportFilter
            onShow={handleShow}
            onExport={handleExportReport}
            onPrint={handlePrintReport}
            loading={loading}
            hasResults={hasResults}
            deviceType="single"
          >
            <Box className={classes.filterItem} sx={{ minWidth: 200 }}>
              <ColumnSelect
                  columns={columns}
                  setColumns={setColumns}
                  columnsArray={columnsArray}
                  rawValues={true}
                  disabled={loading}
                />
            </Box>
          </ReportFilter>
        </div>
      <Table>
        <TableHead>
          <TableRow>
            {columns.map((key) => (
              <TableCell key={key}>
                {t(columnsMap.get(key) || key)}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {!loading ? (
            items.map((item, index) => (
              <TableRow key={`item_${index}_${new Date().getMilliseconds()}`}>
                {columns.map((key) => (
                  <TableCell key={key}>{formatValue(item, key)}</TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableShimmer 
              columns={columns.length} 
              startAction={false} 
              endAction={false}
            />
          )}
        </TableBody>
      </Table>
      </div>
    </div>
  );

  return (
    <PageLayout
      menu={<ReportsMenu />}
      breadcrumbs={["reportTitle", "reportCostsResume"]}
    >
      {pageContent}
    </PageLayout>
  );
};

export default CostsResumeReportPage;
